import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button } from "src/components/base/button";
import { useEffect, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ApiInternal } from "src/api/api_internal";
import {
    DeviceFromIpResponse,
    ExtraDnsRecordType,
    FullExtraDnsDomain,
    FullExtraDnsRecord,
} from "src/api/generated/frontend_internal_v1";
import { DeviceLinkBadge } from "src/components/badges/device-link";
import { Divider } from "src/components/base/divider";
import { TFunction } from "i18next";

/**
 * Properties for {@link UpdateExtraDomainDialog}.
 */
export type UpdateExtraDomainDialogProps = {
    /** The domain to edit (form is reset whenever this changes). */
    dns: FullExtraDnsDomain | undefined;
    /** Whether the dialog is open. */
    isOpen: boolean;
    /** Callback when the dialog is dismissed without submitting. */
    onClose: () => void;
    /** Callback after the diff has been applied successfully. */
    onSubmit: () => void;
};

/**
 * Edit a single extra DNS domain in one dialog: name + description + IPv4 + IPv6.
 *
 * Tailscale's client only resolves one A and one AAAA per domain, so the data model
 * collapses to (ipv4?, ipv6?). On submit we diff against the current record set and
 * issue add/update/delete API calls per record type as needed.
 */
export default function UpdateExtraDomainDialog(props: UpdateExtraDomainDialogProps) {
    const [t] = useTranslation("dialog-update-extra-domain");
    const dns = props.dns;
    const aRecord = findRecord(dns, ExtraDnsRecordType.A);
    const aaaaRecord = findRecord(dns, ExtraDnsRecordType.AAAA);

    const form = useForm({
        defaultValues: {
            domainName: dns?.from_dns ?? "",
            description: dns?.description ?? "",
            ipv4: aRecord?.to_ip ?? "",
            ipv6: aaaaRecord?.to_ip ?? "",
        },
        validators: {
            onChange: ({ value }) => {
                if (value.ipv4.trim().length === 0 && value.ipv6.trim().length === 0) {
                    const msg = t("error.at-least-one-ip");
                    return { fields: { ipv4: msg, ipv6: msg } };
                }
                return undefined;
            },
            onSubmitAsync: async ({ value }) => {
                if (!dns) return;

                const v4 = value.ipv4.trim();
                const v6 = value.ipv6.trim();

                // 1. Domain name / description.
                if (value.domainName !== dns.from_dns || value.description !== dns.description) {
                    const res = await ApiInternal.dns.updateExtraDnsDomain(dns.uuid, {
                        from_dns: value.domainName,
                        description: value.description,
                    });
                    if (res?.result === "Err") {
                        return {
                            fields: {
                                domainName: res.error.domain_invalid
                                    ? t("error.invalid-domain")
                                    : res.error.domain_already_exists
                                      ? t("error.domain-exists")
                                      : res.error.device_already_exists
                                        ? t("error.device-exists")
                                        : undefined,
                            },
                        };
                    }
                }

                // 2. Diff IPv4 record.
                const aErr = await syncRecord(dns.uuid, aRecord, v4, ExtraDnsRecordType.A, t);
                if (aErr) return { fields: { ipv4: aErr } };

                // 3. Diff IPv6 record.
                const aaaaErr = await syncRecord(dns.uuid, aaaaRecord, v6, ExtraDnsRecordType.AAAA, t);
                if (aaaaErr) return { fields: { ipv6: aaaaErr } };

                props.onSubmit();
            },
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, dns?.uuid]);

    const formIpv4 = useStore(form.store, (s) => s.values.ipv4);
    const formIpv6 = useStore(form.store, (s) => s.values.ipv6);
    const v4Device = useDeviceForIp(formIpv4);
    const v6Device = useDeviceForIp(formIpv6);

    if (!dns) return null;

    return (
        <Dialog onClose={props.onClose} open={props.isOpen}>
            <Form onSubmit={form.handleSubmit}>
                <DialogTitle>{t("heading.update-extra-domain")}</DialogTitle>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"domainName"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            maxLength={255}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={String(err)}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Input
                                            maxLength={255}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                    </Field>
                                )}
                            </form.Field>
                            <Divider />
                            <Description>{t("description.edit-ip-hint")}</Description>
                            <form.Field name={"ipv4"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.ipv4")}</Label>
                                        <Input
                                            maxLength={255}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            placeholder={t("hint.empty-removes")}
                                        />
                                        {v4Device && (
                                            <DeviceLinkBadge className={"mt-2"} device={v4Device} target={"_blank"} />
                                        )}
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={String(err)}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"ipv6"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.ipv6")}</Label>
                                        <Input
                                            maxLength={255}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            placeholder={t("hint.empty-removes")}
                                        />
                                        {v6Device && (
                                            <DeviceLinkBadge className={"mt-2"} device={v6Device} target={"_blank"} />
                                        )}
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={String(err)}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("button.cancel")}</Button>
                    <Button type={"submit"} color={"blue"}>
                        {t("button.update")}
                    </Button>
                </DialogActions>
            </Form>
        </Dialog>
    );
}

/**
 * Returns the existing record on `dns` of the given type, if any.
 *
 * @param dns the domain to search
 * @param type the record type to look for
 * @returns the matching record, or `undefined`
 */
function findRecord(dns: FullExtraDnsDomain | undefined, type: ExtraDnsRecordType) {
    return dns?.records.find((r) => r.record_type === type);
}

/**
 * Add / update / delete a single record so it matches `target` (empty string = delete).
 *
 * @param domainUuid uuid of the domain the record belongs to
 * @param existing the current record on the domain (if any)
 * @param target the desired IP value; empty string means "no record of this type"
 * @param type record type (A or AAAA)
 * @param t translation function usable to convert error messages properly
 * @returns an i18n-compatible error message on failure, or undefined on success
 */
async function syncRecord(
    domainUuid: string,
    existing: FullExtraDnsRecord | undefined,
    target: string,
    type: ExtraDnsRecordType,
    t: TFunction,
): Promise<string | undefined> {
    if (existing && target === "") {
        await ApiInternal.dns.deleteExtraDnsRecord(existing.uuid);
        return undefined;
    }
    if (existing && target !== existing.to_ip) {
        const res = await ApiInternal.dns.updateExtraDnsRecord(existing.uuid, {
            record_type: type,
            to_ip: target,
        });
        if (res.result === "Err") return mapRecordError(res.error, t);
        return undefined;
    }
    if (!existing && target !== "") {
        const res = await ApiInternal.dns.addSingleExtraDnsRecord(domainUuid, {
            record_type: type,
            to_ip: target,
        });
        if (res.result === "Err") return mapRecordError(res.error, t);
        return undefined;
    }
    return undefined;
}

/**
 * Subset of the API error shape relevant for record add / update calls.
 */
type RecordError = {
    /** True if the IP didn't parse / didn't match the record type. */
    ip_invalid?: boolean;
    /** True if the IP isn't reachable from the internal range. */
    no_internal_device?: boolean;
    /** True if the IP is already used on another record. */
    ip_address_exists?: boolean;
};

/**
 * Maps an API record error to its localized message key.
 *
 * @param err error payload returned by the API
 * @param t translation function usable to convert error messages properly
 * @returns an i18n key, or `undefined` if no field matched
 */
function mapRecordError(err: RecordError, t: TFunction) {
    if (err.ip_invalid) return t("error.invalid-ip");
    if (err.no_internal_device) return t("error.no-internal-device");
    if (err.ip_address_exists) return t("error.ip-address-already-exists");
    return undefined;
}

/**
 * Looks up the internal device that owns `ip`, debounced. Returns `undefined` for empty
 * input or if no device matches the address.
 *
 * @param ip the IP address to resolve to a device
 * @returns the matching device, or `undefined`
 */
function useDeviceForIp(ip: string): DeviceFromIpResponse | undefined {
    const [device, setDevice] = useState<DeviceFromIpResponse | undefined>();
    useEffect(() => {
        if (ip.trim() === "") {
            setDevice(undefined);
            return;
        }
        const t = setTimeout(async () => {
            setDevice(await ApiInternal.devices.getFromIp(ip));
        }, 500);
        return () => clearTimeout(t);
    }, [ip]);
    return device;
}
